import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Grid,
} from "@mui/material";
import {
  useAssignExamToClassesMutation,
  useGetExamAssignedClassesQuery,
  useRemoveExamClassAssignmentMutation,
} from "@/store/api/adminApi";
import DeleteIcon from "@mui/icons-material/Delete";
import SchoolIcon from "@mui/icons-material/School";
import GroupIcon from "@mui/icons-material/Group";
import { ConfirmDialog } from "./ConfirmDialog";
import ClassMultiSelect from "./ClassMultiSelect";

const ExamAssignmentDialog = React.memo(
  ({ open, onClose, exam }) => {
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [assignmentError, setAssignmentError] = useState("");
    const [assignmentSuccess, setAssignmentSuccess] = useState("");
    const [dialogDelete, setDialogDelete] = useState({
      open: false,
      classId: null,
      title: "",
      message: "",
      confirmText: "",
      cancelText: "",
    });

    const {
      data: assignedClassesData,
      isLoading: assignedClassesLoading,
      refetch: refetchAssignedClasses,
    } = useGetExamAssignedClassesQuery(exam?._id, {
      skip: !exam?._id || !open,
    });

    const [assignExamToClasses, { isLoading: isAssigning }] =
      useAssignExamToClassesMutation();
    const [removeExamClassAssignment, { isLoading: isRemoving }] =
      useRemoveExamClassAssignmentMutation();

    useEffect(() => {
      if (open) {
        setSelectedClasses([]);
        setAssignmentError("");
        setAssignmentSuccess("");
      }
    }, [open]);

    const handleAssignClasses = useCallback(async () => {
      if (selectedClasses.length === 0) {
        setAssignmentError("Please select at least one class to assign.");
        return;
      }

      try {
        await assignExamToClasses({
          examId: exam._id,
          classIds: selectedClasses.map((classItem) => classItem._id),
        }).unwrap();

        setAssignmentSuccess(
          `Successfully assigned exam to ${selectedClasses.length} class(es).`
        );
        setSelectedClasses([]);
        refetchAssignedClasses();
        setAssignmentError("");
      } catch (error) {
        console.error("Error assigning exam:", error);
        setAssignmentError(
          error?.data?.message || "Failed to assign exam to classes."
        );
        setAssignmentSuccess("");
      }
    }, [
      selectedClasses,
      exam?._id,
      assignExamToClasses,
      refetchAssignedClasses,
    ]);

    const confirmToDeleteAssignment = useCallback(async (classId) => {
      setDialogDelete({
        open: true,
        classId,
        title: "Confirm to remove assignment",
        onClose: () => setDialogDelete({ open: false }),
        message: `Are you sure you want to remove this exam assignment from the class?`,
        confirmText: "Yes",
        cancelText: "No",
      });
    }, []);

    const handleRemoveAssignment = useCallback(
      async () => {
        const { classId } = dialogDelete;
        setDialogDelete({ open: false });
        try {
          await removeExamClassAssignment({
            examId: exam._id,
            classId,
          }).unwrap();

          refetchAssignedClasses();
          setAssignmentSuccess("Class assignment removed successfully.");
          setAssignmentError("");
        } catch (error) {
          console.error("Error removing assignment:", error);
          setAssignmentError("Failed to remove class assignment.");
          setAssignmentSuccess("");
        }
      },
      [exam?._id, dialogDelete, removeExamClassAssignment, refetchAssignedClasses]
    );

    const assignedClasses = useMemo(
      () => assignedClassesData?.data || [],
      [assignedClassesData?.data]
    );

    // Each assignment has its class populated under `class`
    const assignedClassIds = useMemo(
      () => assignedClasses.map((assignment) => assignment.class?._id),
      [assignedClasses]
    );

    if (!exam) return null;

    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
              color: "white",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GroupIcon />
              <Typography variant="h6">Assign Exam: {exam.title}</Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {(assignmentError || assignmentSuccess) && (
              <Box sx={{ mb: 2 }}>
                {assignmentError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {assignmentError}
                  </Alert>
                )}
                {assignmentSuccess && (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    {assignmentSuccess}
                  </Alert>
                )}
              </Box>
            )}

            <Grid container spacing={3}>
              {/* Assign New Classes Section */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ my: 2, color: "#1976D2", fontWeight: 600 }}
                >
                  <SchoolIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Assign to Classes
                </Typography>

                <ClassMultiSelect
                  value={selectedClasses}
                  onChange={setSelectedClasses}
                  excludeIds={assignedClassIds}
                  emptyMessage="All classes are already assigned to this exam"
                />

                <Button
                  variant="contained"
                  onClick={handleAssignClasses}
                  disabled={isAssigning || selectedClasses.length === 0}
                  startIcon={
                    isAssigning ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SchoolIcon />
                    )
                  }
                  fullWidth
                  sx={{
                    background:
                      "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                    py: 1.5,
                    borderRadius: 2,
                  }}
                >
                  {isAssigning
                    ? "Assigning..."
                    : `Assign to ${selectedClasses.length} Class(es)`}
                </Button>
              </Grid>

              {/* Currently Assigned Classes Section */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#1976D2", fontWeight: 600 }}
                >
                  <GroupIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Currently Assigned ({assignedClasses.length})
                </Typography>

                {assignedClassesLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : assignedClasses.length === 0 ? (
                  <Alert severity="info">
                    No classes assigned to this exam yet
                  </Alert>
                ) : (
                  <List
                    sx={{
                      bgcolor: "background.paper",
                      borderRadius: 2,
                      border: "1px solid rgba(0,0,0,0.12)",
                      maxHeight: 300,
                      overflow: "auto",
                    }}
                  >
                    {assignedClasses.map((classItem, index) => (
                      <React.Fragment key={classItem._id}>
                        {index > 0 && <Divider />}
                        <ListItem>
                          <ListItemText
                            primary={classItem?.class?.classCode}
                            secondary={
                              classItem?.class?.className ||
                              `${classItem?.class?.studentCount || 0} students`
                            }
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() =>
                                confirmToDeleteAssignment(classItem.class?._id)
                              }
                              disabled={isRemoving}
                              sx={{
                                color: "#f44336",
                                "&:hover": {
                                  bgcolor: "rgba(244, 67, 54, 0.08)",
                                },
                              }}
                            >
                              {isRemoving ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon />
                              )}
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={onClose}
              sx={{
                color: "#666",
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <ConfirmDialog
          open={dialogDelete.open}
          onClose={dialogDelete.onClose}
          title="Confirm Assignment"
          message={dialogDelete.message}
          onConfirm={handleRemoveAssignment}
          confirmText={dialogDelete.confirmText}
          cancelText={dialogDelete.cancelText}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    return (
      prevProps.open === nextProps.open &&
      prevProps.onClose === nextProps.onClose &&
      prevProps.exam?._id === nextProps.exam?._id
    );
  }
);

ExamAssignmentDialog.displayName = "ExamAssignmentDialog";

export default ExamAssignmentDialog;
